// viewer.freeze.exif.js — a minimal EXIF writer for freeze frames.
//
// canvas.toBlob() produces a bare JPEG: no timestamp, no geotag. A freeze
// frame is a client deliverable (Package C), so it has to say when and where
// it was taken in the metadata every photo tool reads — not only in the
// burnt-in caption. This module builds one APP1 "Exif" segment (little-endian
// TIFF: IFD0 → Exif IFD + GPS IFD) and splices it straight after the JPEG's
// SOI marker. Pure byte work, no DOM, so it runs under `node --test`.
//
// What gets written:
//   IFD0     ImageDescription, Software, DateTime
//   Exif     DateTimeOriginal (local wall clock), OffsetTimeOriginal (+HH:MM),
//            UserComment (ASCII)
//   GPS      VersionID, Latitude/Longitude (+Ref), TimeStamp + DateStamp
//            (UTC), MapDatum — omitted entirely when there is no position.
//
// The date/time fields describe the DEPICTED moment (video start + playhead),
// not when the reviewer pressed Freeze.

const ASCII = 2, LONG = 4, RATIONAL = 5, BYTE = 1, UNDEFINED = 7;
const TYPE_SIZE = { [BYTE]: 1, [ASCII]: 1, [LONG]: 4, [RATIONAL]: 8, [UNDEFINED]: 1 };

// IFD0
const TAG_IMAGE_DESCRIPTION = 0x010e;
const TAG_SOFTWARE = 0x0131;
const TAG_DATETIME = 0x0132;
const TAG_EXIF_IFD = 0x8769;
const TAG_GPS_IFD = 0x8825;
// Exif IFD
const TAG_DATETIME_ORIGINAL = 0x9003;
const TAG_OFFSET_TIME_ORIGINAL = 0x9011;
const TAG_USER_COMMENT = 0x9286;
// GPS IFD
const TAG_GPS_VERSION = 0x0000;
const TAG_GPS_LAT_REF = 0x0001;
const TAG_GPS_LAT = 0x0002;
const TAG_GPS_LON_REF = 0x0003;
const TAG_GPS_LON = 0x0004;
const TAG_GPS_TIME = 0x0007;
const TAG_GPS_MAP_DATUM = 0x0012;
const TAG_GPS_DATE = 0x001d;

const TIFF_HEADER_LEN = 8;
const ENTRY_LEN = 12;

// ---------- value encoders (each yields {type, count, data}) ----------

/** Fold to 7-bit ASCII: EXIF's ASCII fields have no charset, and a UTF-8
 *  em dash renders as mojibake in most readers. Accents drop their marks,
 *  dashes and curly quotes straighten, anything else becomes "?". */
export function toAscii(s) {
  return String(s)
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")          // combining marks
    .replace(/[\u2010-\u2015\u2212]/g, "-")    // hyphens, en/em dashes, minus
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/[\u201c\u201d]/g, '"')
    .replace(/[^\x20-\x7e]/g, "?");
}

function asciiValue(s) {
  const text = toAscii(s);
  const data = new Uint8Array(text.length + 1); // trailing NUL
  for (let i = 0; i < text.length; i++) data[i] = text.charCodeAt(i);
  return { type: ASCII, count: data.length, data };
}

function byteValue(arr) {
  return { type: BYTE, count: arr.length, data: Uint8Array.from(arr) };
}

function longValue(v) {
  const data = new Uint8Array(4);
  new DataView(data.buffer).setUint32(0, v >>> 0, true);
  return { type: LONG, count: 1, data };
}

function rationalValue(pairs) {
  const data = new Uint8Array(pairs.length * 8);
  const dv = new DataView(data.buffer);
  pairs.forEach(([num, den], i) => {
    dv.setUint32(i * 8, num >>> 0, true);
    dv.setUint32(i * 8 + 4, den >>> 0, true);
  });
  return { type: RATIONAL, count: pairs.length, data };
}

/** UserComment: an 8-byte character-code header, then the text. */
function userCommentValue(s) {
  const text = toAscii(s);
  const data = new Uint8Array(8 + text.length);
  data.set([0x41, 0x53, 0x43, 0x49, 0x49, 0, 0, 0]); // "ASCII\0\0\0"
  for (let i = 0; i < text.length; i++) data[8 + i] = text.charCodeAt(i);
  return { type: UNDEFINED, count: data.length, data };
}

// ---------- domain → EXIF values ----------

/** Degrees → three RATIONALs: whole degrees, whole minutes, seconds/10000.
 *  0.0001 s of arc is ~3 mm, well inside the ring's metre-scale precision. */
export function toDms(deg) {
  const v = Math.abs(deg);
  let d = Math.floor(v);
  let m = Math.floor((v - d) * 60);
  let s = Math.round(((v - d) * 60 - m) * 60 * 10000);
  if (s >= 60 * 10000) { s -= 60 * 10000; m += 1; }
  if (m >= 60) { m -= 60; d += 1; }
  return [[d, 1], [m, 1], [s, 10000]];
}

/** Wall-clock parts of `epochS` in `tz`, plus the zone's UTC offset then. */
function localParts(epochS, tz) {
  const parts = new Intl.DateTimeFormat("en-GB", {
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
    hourCycle: "h23", timeZone: tz,
  }).formatToParts(new Date(epochS * 1000));
  const p = Object.fromEntries(parts.map((x) => [x.type, Number(x.value)]));
  const asUtcMs = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  const offsetMin = Math.round((asUtcMs - Math.floor(epochS) * 1000) / 60000);
  return { ...p, offsetMin };
}

const pad2 = (n) => String(n).padStart(2, "0");

function exifDateTime(p) {
  return `${p.year}:${pad2(p.month)}:${pad2(p.day)} ${pad2(p.hour)}:${pad2(p.minute)}:${pad2(p.second)}`;
}

function exifOffset(offsetMin) {
  const sign = offsetMin < 0 ? "-" : "+";
  const a = Math.abs(offsetMin);
  return `${sign}${pad2(Math.floor(a / 60))}:${pad2(a % 60)}`;
}

function utcParts(epochS) {
  const d = new Date(epochS * 1000);
  return {
    year: d.getUTCFullYear(), month: d.getUTCMonth() + 1, day: d.getUTCDate(),
    hour: d.getUTCHours(), minute: d.getUTCMinutes(), second: d.getUTCSeconds(),
  };
}

// ---------- TIFF / IFD layout ----------

const padEven = (n) => n + (n % 2);

/** Bytes an IFD occupies: entry table, next-IFD pointer, out-of-line data. */
function ifdSize(entries) {
  let size = 2 + entries.length * ENTRY_LEN + 4;
  for (const e of entries) if (e.data.length > 4) size += padEven(e.data.length);
  return size;
}

/** Write one IFD at `offset` (a TIFF-relative byte offset). Entries are
 *  sorted by tag, as the spec requires; values over 4 bytes go after the
 *  pointer, word aligned. */
function writeIfd(buf, dv, offset, entries) {
  const sorted = [...entries].sort((a, b) => a.tag - b.tag);
  dv.setUint16(offset, sorted.length, true);
  let dataAt = offset + 2 + sorted.length * ENTRY_LEN + 4;
  sorted.forEach((e, i) => {
    const at = offset + 2 + i * ENTRY_LEN;
    dv.setUint16(at, e.tag, true);
    dv.setUint16(at + 2, e.type, true);
    dv.setUint32(at + 4, e.count, true);
    if (e.data.length <= 4) {
      buf.set(e.data, at + 8); // left-justified, zero padded
    } else {
      dv.setUint32(at + 8, dataAt, true);
      buf.set(e.data, dataAt);
      dataAt += padEven(e.data.length);
    }
  });
  dv.setUint32(offset + 2 + sorted.length * ENTRY_LEN, 0, true); // no next IFD
}

const entry = (tag, v) => ({ tag, ...v });
const hasText = (s) => typeof s === "string" && s.length > 0;
const isLatLon = (lat, lon) => Number.isFinite(lat) && Number.isFinite(lon);

/**
 * Build a complete APP1 segment (marker + length + "Exif\0\0" + TIFF).
 *
 * @param {object} f
 * @param {number} f.utcS          Depicted moment, epoch seconds.
 * @param {string} f.tz            IANA zone the local DateTimeOriginal is in.
 * @param {number|null} f.lat      Geotag; both null/NaN → no GPS IFD.
 * @param {number|null} f.lon
 * @param {string} [f.description] ImageDescription.
 * @param {string} [f.software]    Software.
 * @param {string} [f.userComment] UserComment (ASCII).
 * @returns {Uint8Array}
 */
export function buildExifApp1(f) {
  const local = localParts(f.utcS, f.tz);
  const utc = utcParts(f.utcS);
  const stamp = exifDateTime(local);

  const ifd0 = [entry(TAG_DATETIME, asciiValue(stamp))];
  if (hasText(f.description)) ifd0.push(entry(TAG_IMAGE_DESCRIPTION, asciiValue(f.description)));
  if (hasText(f.software)) ifd0.push(entry(TAG_SOFTWARE, asciiValue(f.software)));

  const exif = [
    entry(TAG_DATETIME_ORIGINAL, asciiValue(stamp)),
    entry(TAG_OFFSET_TIME_ORIGINAL, asciiValue(exifOffset(local.offsetMin))),
  ];
  if (hasText(f.userComment)) exif.push(entry(TAG_USER_COMMENT, userCommentValue(f.userComment)));

  const gps = isLatLon(f.lat, f.lon) ? [
    entry(TAG_GPS_VERSION, byteValue([2, 3, 0, 0])),
    entry(TAG_GPS_LAT_REF, asciiValue(f.lat < 0 ? "S" : "N")),
    entry(TAG_GPS_LAT, rationalValue(toDms(f.lat))),
    entry(TAG_GPS_LON_REF, asciiValue(f.lon < 0 ? "W" : "E")),
    entry(TAG_GPS_LON, rationalValue(toDms(f.lon))),
    entry(TAG_GPS_TIME, rationalValue([[utc.hour, 1], [utc.minute, 1], [utc.second, 1]])),
    entry(TAG_GPS_MAP_DATUM, asciiValue("WGS-84")),
    entry(TAG_GPS_DATE, asciiValue(`${utc.year}:${pad2(utc.month)}:${pad2(utc.day)}`)),
  ] : null;

  // Pointer entries are 4-byte LONGs, so IFD0's size does not depend on the
  // values they hold — lay the blocks out, then fill the pointers in.
  ifd0.push(entry(TAG_EXIF_IFD, longValue(0)));
  if (gps) ifd0.push(entry(TAG_GPS_IFD, longValue(0)));
  const exifOff = TIFF_HEADER_LEN + ifdSize(ifd0);
  const gpsOff = exifOff + ifdSize(exif);
  const tiffLen = gps ? gpsOff + ifdSize(gps) : gpsOff;
  ifd0.find((e) => e.tag === TAG_EXIF_IFD).data = longValue(exifOff).data;
  if (gps) ifd0.find((e) => e.tag === TAG_GPS_IFD).data = longValue(gpsOff).data;

  const tiff = new Uint8Array(tiffLen);
  const dv = new DataView(tiff.buffer);
  tiff.set([0x49, 0x49]);          // "II" little-endian
  dv.setUint16(2, 42, true);
  dv.setUint32(4, TIFF_HEADER_LEN, true);
  writeIfd(tiff, dv, TIFF_HEADER_LEN, ifd0);
  writeIfd(tiff, dv, exifOff, exif);
  if (gps) writeIfd(tiff, dv, gpsOff, gps);

  const segLen = 2 + 6 + tiff.length; // length field counts itself
  if (segLen > 0xffff) throw new Error("EXIF segment exceeds 64 KiB");
  const app1 = new Uint8Array(2 + segLen);
  app1.set([0xff, 0xe1, segLen >> 8, segLen & 0xff, 0x45, 0x78, 0x69, 0x66, 0, 0]);
  app1.set(tiff, 10);
  return app1;
}

/** Splice an APP1 segment in straight after SOI. The canvas encoder emits no
 *  EXIF of its own, so nothing needs stripping. */
export function insertExifIntoJpeg(jpeg, app1) {
  if (!(jpeg instanceof Uint8Array) || jpeg.length < 4 || jpeg[0] !== 0xff || jpeg[1] !== 0xd8) {
    throw new Error("insertExifIntoJpeg: not a JPEG");
  }
  const out = new Uint8Array(jpeg.length + app1.length);
  out.set(jpeg.subarray(0, 2), 0);
  out.set(app1, 2);
  out.set(jpeg.subarray(2), 2 + app1.length);
  return out;
}

/** Blob → Blob convenience for the capture path. */
export async function tagJpegBlob(blob, fields) {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  return new Blob([insertExifIntoJpeg(bytes, buildExifApp1(fields))], { type: "image/jpeg" });
}
